/**
 * Utilities for DeviceModal layout and field rendering.
 *
 * Provides a `getLayout` helper that maps the selected system/device
 * to a layout definition from `data/DeviceTypeForm.json`, plus
 * small renderer helpers used by the modal components.
 */
import layout from 'data/DeviceTypeForm.json';
export { renderField } from './modalDropdownUtils';

/**
 * Resolve the layout config for the currently-selected device type.
 *
 * The function looks up the selected integration (by `formData.system_type`),
 * finds the matching `system_type` entry inside `deviceTypes`, then uses the
 * device type id at `formData.device_type` to select a key into the
 * `data/DeviceTypeForm.json` layout map.
 *
 * @param {object} formData - The local form state (expects `device_type` and `system_type`).
 * @param {Array<object>} deviceTypes - Structured device types array generated by the UI.
 * @returns {object|null} The layout config for the selected device type, or `null` when not found.
 */
export const getLayout = (formData, integrations, deviceTypes) => {
  if (!formData) return null;
  const typeIdx = Number(formData.device_type);
  if (isNaN(typeIdx)) return null;

  // same mapping logic you used in the Device Type dropdown
  const selectedIntegration = integrations?.[Number(formData.system_type)];

  if (!selectedIntegration) return null;

  const sysIdx = deviceTypes.findIndex(
    (sys) => sys.system_type === selectedIntegration.system_type,
  );

  if (sysIdx < 0) return null;

  const sys = deviceTypes[sysIdx];
  const typeObj = sys?.device_types?.[typeIdx];

  const key = typeObj?.type?.toLowerCase().trim();
  if (!key) return null;

  return layout[key] || null;
};

// Helper renderers (`renderField`, `renderInputField`, `renderValidDevice`)
// were moved to `modalDropdownUtils.js` and are re-exported above.

// Limit checker uses the helper
export const isLimitReached = (formData, connectedDevices, systemCount) => {
  const selectedTypeInfo = getSelectedType(formData, connectedDevices);
  return getDeviceType(selectedTypeInfo, connectedDevices, systemCount).limitReached;
};

const getSelectedType = (formData, deviceTypes) => {
  const sysIdx = Number(formData?.system_type);
  const typeIdx = Number(formData?.device_type);
  return deviceTypes?.[sysIdx]?.device_types?.[typeIdx]?.type || null;
};

const getDeviceType = (selected = getSelectedType(), connectedDevices, systemCount) => {
  const connected = connectedDevices || [];
  const counts = systemCount || {};

  // current counts
  const cameraUsed = connected.filter((d) => d.device_type === 'Overwatch VMS').length;
  const mrcUsed = connected.filter((d) => d.device_type === 'Manual Road Closure').length;
  const otherUsed = connected.filter(
    (d) => d.device_type !== 'Manual Road Closure' && d.device_type !== 'Overwatch VMS',
  ).length;

  // caps from system
  const cameraMax = counts?.connected_cameras ?? 0;
  const otherMax = counts?.connected_devices ?? 0;
  const mrcMax = 100;

  // pick which bucket applies to the selected type
  if (selected === 'Manual Road Closure') {
    return {
      key: 'mrc',
      label: 'Manual Road Closure',
      used: mrcUsed,
      max: mrcMax,
      remaining: Math.max(mrcMax - mrcUsed, 0),
      limitReached: mrcUsed >= mrcMax,
    };
  }
  if (selected === 'Overwatch VMS') {
    return {
      key: 'camera',
      label: 'Overwatch VMS',
      used: cameraUsed,
      max: cameraMax,
      remaining: Math.max(cameraMax - cameraUsed, 0),
      limitReached: cameraUsed >= cameraMax,
    };
  }
  // devices bucket = general connected devices
  return {
    key: 'devices',
    label: selected || 'Device',
    used: otherUsed,
    max: otherMax,
    remaining: Math.max(otherMax - otherUsed, 0),
    limitReached: otherUsed >= otherMax,
  };
};
